import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'src/components/system/Button';
import { getSimulationResults, SimulationResultDetail, SimulationResultSummary } from 'src/services/simulations';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 24px;
`;

const Card = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const Row = styled.div<{correct?: boolean; chosen?: boolean}>`
  background: ${({ correct, chosen }) => correct ? '#F1FAF3' : chosen ? '#FFF1F6' : '#F7F7F7'};
  border: 1px solid ${({ correct, chosen }) => correct ? '#5AB56E' : chosen ? '#D81E5B' : '#E0E0E0'};
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AnswerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    gap: 18px;
  }
`;

const AnswerColumn = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AnswerLabel = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b6b6b;
`;

const AnswerText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f1f1f;
  line-height: 1.4;
`;

const QuestionTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #222b82;
`;

export function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories } = useCourseCategories();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<SimulationResultSummary | null>(null);
  const [answers, setAnswers] = useState<SimulationResultDetail[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSimulationResults(Number(id));
        setSummary(data.summary);
        setAnswers(Array.isArray(data.answers) ? data.answers : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Page>Carregando resultados...</Page>;

  const percent = summary ? Math.round((summary.correct / Math.max(1, summary.total)) * 100) : 0;
  const duration = summary ? new Date(summary.duration_seconds * 1000).toISOString().substr(11, 8) : '00:00:00';

  return (
    <Page>
      <h1>Resultado do simulado</h1>
      <Card>
        <p>Acertos: <strong>{summary?.correct ?? 0}</strong> de {summary?.total ?? 0} ({percent}%)</p>
        <p>Tempo: <strong>{duration}</strong></p>
      </Card>
      <Card>
        <h3>Gabarito</h3>
        <Grid>
          {answers.map(answer => {
            const chosenText = (answer.chosen_answer_text ?? '').trim();
            const correctText = (answer.correct_answer_text ?? '').trim();
            const chosenDisplay =
              chosenText !== ''
                ? chosenText
                : answer.chosen_answer_id
                  ? String(answer.chosen_answer_id)
                  : 'Não respondida';
            const correctDisplay =
              correctText !== ''
                ? correctText
                : answer.correct_answer_id
                  ? String(answer.correct_answer_id)
                  : 'Indisponível';

            return (
              <Row
                key={answer.question_id}
                correct={answer.is_correct}
                chosen={Boolean(answer.chosen_answer_id) && !answer.is_correct}
              >
                <QuestionTitle>Questão #{answer.question_id}</QuestionTitle>
                <AnswerWrapper>
                  <AnswerColumn>
                    <AnswerLabel>Alternativa marcada</AnswerLabel>
                    <AnswerText>{chosenDisplay}</AnswerText>
                  </AnswerColumn>
                  <AnswerColumn>
                    <AnswerLabel>Alternativa correta</AnswerLabel>
                    <AnswerText>{correctDisplay}</AnswerText>
                  </AnswerColumn>
                </AnswerWrapper>
              </Row>
            );
          })}
        </Grid>
      </Card>
      <div style={{ display: 'flex', gap: 12 }}>
        <Button
          width="220px"
          backgroundColor="#222B82"
          borderColor="#222B82"
          buttonColor="#fff"
          onClick={() => navigate(`/families/${categories[0]?.slug ?? 'cfp'}`)}
          label={<span>Voltar</span>}
        />
      </div>
    </Page>
  );
}
